import hashUtils from 'app-lib/hashUtils'
import { getBlockExplorerAddressLink, links, ookiToken } from 'bzx-common/src/config/appConfig'
import { stakingConfig } from 'bzx-common/src/config/chains/ethereumConfig'
import { observer } from 'mobx-react'
import React from 'react'
import { Link } from 'react-router-dom'
import ButtonChainSwitch from 'shared-components/ButtonChainSwitch'
import ExternalLink from 'shared-components/ExternalLink'
import { Button } from 'ui-framework'
import StakingVM from './StakingVM'
import imgExitOldStaking from './exit-old-staking.png'

const { stakingContract } = stakingConfig
const contractUrl = getBlockExplorerAddressLink(1, stakingContract)

export function AboutStaking({ vm }: { vm: StakingVM }) {
  return (
    <>
      {vm.rootStore.web3Connection.chainId !== 1 && (
        <div className="txt-center margin-top-3">
          <div className="flex-center margin-bottom-2">
            <ButtonChainSwitch chainId={1}>Stake on Ethereum</ButtonChainSwitch>
          </div>
          <p>You need to be on Ethereum Mainnet to stake.</p>
        </div>
      )}
      <section className="panel--simple padded-2 margin-top-4">
        <h3 className="margin-bottom-1 title-section flex-row-center">
          About Staking
          <div className="flex-row-end font-normal txt-right">
            Contract:{' '}
            <ExternalLink className="link--discreet" href={contractUrl} title={stakingContract}>
              {hashUtils.shortHash(stakingContract, 8, 8)}
            </ExternalLink>
          </div>
        </h3>

        <div className="txt-explanation">
          <ul className="margin-bottom-1">
            <li>
              <b>Earn</b> part of the Ooki protocol fees and{' '}
              <ExternalLink className="link--discreet" href={links.daoSnapshot}>
                vote in DAO proposals
              </ExternalLink>
              .
            </li>
            <li>
              <b>Directly claim rewards</b> for{' '}
              <ExternalLink className="link--discreet" href={ookiToken.slp.sushiPoolLink}>
                providing liquidity
              </ExternalLink>
              .
            </li>
          </ul>

          <div className="note--warning-light padded-v-05 padded-h-1">
            <b>Migration Note</b>: if you were staking BZRX on Fulcrum, you will have to unstake
            first from the Fulcrum app and restake here.{' '}
            <Button variant="link" onClick={vm.migrationDetailsDialog.toggle}>
              Please, read the FAQ
            </Button>
            .
          </div>

          {vm.migrationDetailsDialog.visible && (
            <div className="margin-top-2">
              <h3 className="title-section">Staking Migration FAQ</h3>
              <ul>
                <li className="margin-bottom-2">
                  <h4 className="h4">
                    Q: Is there automatic migration for staked BZRX, iBZRX and BZRX/ETH SLP?
                  </h4>
                  <p>
                    No. Due to technical limitations, we have deployed a new staking contract. You
                    will have to unstake from the old contract, migrate and restake.
                  </p>
                  <p>
                    BZRX can be migrated to OOKI on the{' '}
                    <Link className="link--discreet" to="/token-migration">
                      migration page
                    </Link>
                    .
                  </p>
                </li>
                <li className="margin-bottom-2">
                  <h4 className="h4">
                    Q: What should I do if I have staked BZRX, iBZRX or BZRX/ETH SLP?
                  </h4>
                  <p>
                    If you still have them staked in the old staking contract, unstake them first.
                    The address is <b>0xe95ebce2b02ee07def5ed6b53289801f7fc137a4</b>.
                  </p>
                  <ul>
                    <li>
                      Go to the old staking contract page on Etherscan,{' '}
                      <ExternalLink
                        className="link--discreet"
                        href="https://etherscan.io/address/0xe95ebce2b02ee07def5ed6b53289801f7fc137a4#writeProxyContract">
                        "Write as Proxy" section
                      </ExternalLink>
                      .
                    </li>
                    <li>
                      Connect your wallet using the Etherscan "Connect to Web3" button, be sure to
                      be on Ethereum Mainnet.
                    </li>
                    <li>
                      Once connected, locate the <b>"exit"</b> function in the page and use the
                      "write" button.
                    </li>
                    <li>
                      Confirm the transaction and all the tokens will be sent back to your wallet.
                    </li>
                  </ul>
                  <img className="width-100p margin-top-2" src={imgExitOldStaking} loading="lazy" />
                </li>

                <li className="margin-bottom-2">
                  <h4 className="h4">Q: What about staked iBZRX? </h4>
                  <p>
                    The equivalent of iBZRX is{' '}
                    <ExternalLink
                      className="link--discreet"
                      href={ookiToken.iooki.blockExplorerUrl}>
                      iOOKI
                    </ExternalLink>
                    .
                  </p>
                  <ul>
                    <li>Convert BZRX to OOKI and lend /stake again here on Ooki.</li>
                  </ul>
                </li>
                <li className="margin-bottom-2">
                  <h4 className="h4">Q: What about vBZRX?</h4>
                  <p>
                    If staked on the legacy Fulcrum app; you need to unstake them, then restake them
                    here on Ooki.
                  </p>

                  <p>
                    Note: once staked in the new Ooki staking contract, the part that has vested
                    will be automatically converted to OOKI.
                  </p>
                </li>
                <li className="margin-bottom-2">
                  <h4 className="h4">Q: What about Sushi LP token ETH/BZRX?</h4>
                  <p>
                    The equivalent token is the{' '}
                    <ExternalLink
                      className="link--discreet"
                      title={ookiToken.slp.address}
                      href={ookiToken.slp.blockExplorerUrl}>
                      SLP OOKI/ETH token
                    </ExternalLink>
                    .
                  </p>
                  <ul>
                    <li>Unstake them, and remove the liquidity from Sushi to get BZRX back.</li>
                    <li>
                      Convert BZRX to OOKI add liquidity on{' '}
                      <ExternalLink className="link--discreet" href={ookiToken.slp.sushiPoolLink}>
                        SUSHI LP OOKI/ETH
                      </ExternalLink>
                      .
                    </li>
                    <li>{"Restake your SLP's here on Ooki."}</li>
                  </ul>
                </li>
              </ul>
              <p>
                <b>Note</b>: be aware that the gas cost on Ethereum may be high for all these steps.
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  )
}

export default observer(AboutStaking)
